import type { ReactElement } from 'react'
import { Button } from '~/components/ui/Controls'
import { Modal } from '~/components/ui/Modal'
import { clearInvitationFromLocation, useAcceptInvitation } from '~/state/auth'

/**
 * The other end of the dead link `PRO-169` names: a visitor who already had an
 * account arrives signed in, with a token registering could not use. Nothing
 * else in the product surfaces that a link like this was followed, so this is
 * the one place that says so and offers the one call that finishes it.
 *
 * Shown for every signed-in arrival that still carries `?invitation=`,
 * regardless of how the session came to be signed in — a fresh sign-in, a tab
 * already open, or another device that never left. The one case it must not
 * fire for is a registration that has just redeemed this same token itself;
 * `App` clears the address before this can mount for that one.
 */
export function AcceptInvitation({
  token,
  email,
  onDone,
}: {
  token: string
  /** The signed-in address, printed so a wrong-address refusal reads as an explanation rather than a bug. */
  email: string
  onDone: () => void
}): ReactElement {
  const accept = useAcceptInvitation()

  const dismiss = (): void => {
    clearInvitationFromLocation()
    onDone()
  }

  if (accept.isSuccess) {
    return (
      <Modal
        title="Joined"
        onClose={dismiss}
        footer={
          <Button kind="primary" onClick={dismiss}>
            Continue
          </Button>
        }
      >
        <p className="nw-flag nw-flag--granted">
          <span className="nw-flag-icon" aria-hidden="true">
            ✓
          </span>
          <span>
            You are in. The household's figures are open to <b>{email}</b> from here, alongside
            whichever ones you already had.
          </span>
        </p>
      </Modal>
    )
  }

  return (
    <Modal
      title="You have been invited"
      lede={`Signed in as ${email}.`}
      onClose={dismiss}
      footer={
        <>
          <Button onClick={dismiss} disabled={accept.isPending}>
            Not now
          </Button>
          <Button kind="primary" onClick={() => accept.mutate({ token })} disabled={accept.isPending}>
            {accept.isPending ? 'Joining…' : 'Accept and join'}
          </Button>
        </>
      }
    >
      <p>
        Accepting adds this address to the household the invitation names — its figures, not a
        separate copy of them — and everyone already in it will see your name alongside theirs. If
        this was not meant for you, dismissing it spends nothing.
      </p>

      {accept.error && (
        <p className="nw-flag nw-flag--missing" style={{ marginTop: 16 }}>
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>{describeFailure(accept.error, email)}</span>
        </p>
      )}
    </Modal>
  )
}

function describeFailure(error: Error, email: string): string {
  if (error.message === 'invitation_invalid') {
    return `Could not accept — the invitation is spent, expired, or was written to an address other than ${email}. Ask whoever sent it for another.`
  }
  return `Could not accept: ${error.message}`
}
